import React, { useState } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/CheckOutlined';

import { CohortIdentity, cohortLabel, cohortTechnicalIdentity } from '../../lib/cohortLabel';
import { colors } from '../../theme/tokens';

/**
 * Canonical cohort display — friendly label at rest, technical identity (name +
 * coh- code) on hover/focus and copyable. Reused EVERYWHERE a cohort renders so
 * non-technical users never see raw identifiers, while power users stay one hover
 * away. DISPLAY ONLY — callers keep passing the technical code/name unchanged.
 */
interface Props {
  cohort: CohortIdentity;
  /** 'text' = inline label (default); 'pill' = bordered chip (Import tree style). */
  variant?: 'text' | 'pill';
  /** Muted styling (e.g. unlinked cohorts in the import tree). */
  muted?: boolean;
  /** Optional trailing element, e.g. a status chip. */
  status?: React.ReactNode;
  /** Max width before ellipsis truncation. */
  maxWidth?: number;
}

const TechnicalTooltip: React.FC<{ cohort: CohortIdentity }> = ({ cohort }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cohortTechnicalIdentity(cohort));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
      <Box sx={{ fontFamily: 'Fira Code, Consolas, monospace', fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
        {cohort.name ? <div>{cohort.name}</div> : null}
        {cohort.code ? <div style={{ opacity: 0.8 }}>{cohort.code}</div> : null}
        {!cohort.name && !cohort.code ? <div>(no technical identifier)</div> : null}
      </Box>
      <IconButton
        size="small"
        onClick={copy}
        aria-label="Copy cohort identifier"
        sx={{ color: 'inherit', p: 0.25 }}
      >
        {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
      </IconButton>
    </Box>
  );
};

export default function CohortLabel({ cohort, variant = 'text', muted, status, maxWidth = 280 }: Props) {
  const label = cohortLabel(cohort);

  const labelEl = (
    <Box
      component="span"
      tabIndex={0}
      sx={{
        display: 'inline-block',
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'bottom',
        cursor: 'default',
        ...(variant === 'pill'
          ? {
              fontSize: 12,
              fontFamily: 'Manrope, sans-serif',
              color: muted ? '#A7AEBC' : '#3B414E',
              backgroundColor: muted ? '#F7F8FA' : '#FFFFFF',
              border: `1px solid ${muted ? '#EDEFF3' : '#E8EAEF'}`,
              borderRadius: '8px',
              px: 1.25,
              py: 0.5,
              flexShrink: 0,
            }
          : {
              fontSize: 13,
              color: muted ? colors.textTertiary : colors.textPrimary,
            }),
      }}
    >
      {label}
    </Box>
  );

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth }}>
      <Tooltip title={<TechnicalTooltip cohort={cohort} />} arrow placement="top" enterDelay={300} disableInteractive={false}>
        {labelEl}
      </Tooltip>
      {status}
    </Box>
  );
}
